<script setup lang="ts">
import { computed, ref } from 'vue'

import { COMPONENTS, type CatalogueEntry, FOUNDATIONS } from '#design/lib/catalogue'
import PageHeader from '#shared/components/PageHeader.vue'
import { Input } from '#ui/input'
import { Label } from '#ui/label'

definePage({
  meta: { requiresAuth: true, title: 'Design system' },
})

const query = ref('')

const matches = (entry: CatalogueEntry) =>
  entry.title.toLowerCase().includes(query.value.trim().toLowerCase())

const groups = computed(() => [
  { label: 'Foundations', entries: FOUNDATIONS.filter(matches) },
  { label: 'Components', entries: COMPONENTS.filter(matches) },
])

const shown = computed(() => groups.value.flatMap((group) => group.entries))
</script>

<template>
  <PageHeader
    title="Design system"
    description="Every token and component the admin is built from, rendered live against the real stylesheet. Both themes are the same components, so a specimen that only holds up in one is a bug."
  />

  <div class="flex flex-col gap-2 sm:max-w-xs">
    <Label for="design-search">Search components</Label>
    <Input id="design-search" v-model="query" placeholder="Search components" />
  </div>

  <div class="flex gap-8">
    <nav
      aria-label="Catalogue"
      class="sticky top-6 hidden max-h-[calc(100svh-7.5rem)] w-44 shrink-0 flex-col gap-5 self-start overflow-y-auto pr-1 lg:flex"
    >
      <section v-for="group in groups" :key="group.label" :aria-label="group.label">
        <p class="mb-1.5 px-2 text-xs font-medium text-muted-foreground">{{ group.label }}</p>
        <a
          v-for="entry in group.entries"
          :key="entry.id"
          :href="`#${entry.id}`"
          class="flex h-7 items-center rounded-md px-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {{ entry.title }}
        </a>
      </section>
    </nav>

    <div class="flex min-w-0 flex-1 flex-col gap-12">
      <section
        v-for="entry in shown"
        :id="entry.id"
        :key="entry.id"
        class="flex scroll-mt-6 flex-col gap-4"
      >
        <h2 class="text-xl font-semibold tracking-tight">{{ entry.title }}</h2>
        <component :is="entry.body" />
      </section>

      <p v-if="!shown.length" class="text-sm text-muted-foreground">
        No component matches “{{ query.trim() }}”.
      </p>
    </div>
  </div>
</template>
